import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { MutedAlert } from '@thoughtspot/radiant-react/widgets/alert/muted/muted-alert';
import { ContentGrid, Horizontal, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator/loading-indicator';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigPrePostFixStrings } from '../../settings/common';
import { ConfigCard, InputType, SectionContainer, } from '../config-components';
import { FeatureManagementTranslationKeys } from '../constants';
import { DisableFeatureModal } from './disable-feature-modal';
import styles from './feature-management.module.scss';
import { FeatureAccessTab, getFeatureDisplayName, } from './feature-management-table.util';
const noop = () => { };
export const FeatureManagement = ({ loading = false, features = [], allOrgs = [], selectedTab = FeatureAccessTab.GeneralAccess, onSaveFeatureOrgs = noop, onSearch = noop, onToggleFeature = noop, }) => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [disableModalState, setDisableModalState] = useState({ isOpen: false, feature: null });
    useEffect(() => {
        setSearchValue('');
        onSearch('');
    }, [selectedTab, onSearch]);
    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
        onSearch(value);
    }, [onSearch]);
    const getOrgIds = useCallback((feature) => {
        if (!feature.orgs)
            return [];
        return allOrgs
            .filter(org => feature.orgs.includes(org.name))
            .map(org => org.id);
    }, [allOrgs]);
    const makeSaveHandler = useCallback((feature) => (orgIds) => {
        const featureName = getFeatureDisplayName(feature, t);
        onSaveFeatureOrgs(feature.id, orgIds, () => {
            if (selectedTab === FeatureAccessTab.EarlyAccess &&
                orgIds.length > 0) {
                onToggleFeature(feature.id, true);
            }
            setToastMessage(t(FeatureManagementTranslationKeys.TOAST_ORGS_SETTINGS_UPDATED, { featureName }));
            setIsToastVisible(true);
        });
    }, [t, onSaveFeatureOrgs, selectedTab, onToggleFeature]);
    const saveHandlers = useMemo(() => new Map(features.map(f => [f.id, makeSaveHandler(f)])), [features, makeSaveHandler]);
    const handleDisableTrigger = useCallback((feature) => {
        setDisableModalState({ isOpen: true, feature });
    }, []);
    const handleDisableConfirm = useCallback(() => {
        const { feature } = disableModalState;
        if (!feature)
            return;
        onToggleFeature(feature.id, false);
        onSaveFeatureOrgs(feature.id, [], () => {
            setToastMessage(t(FeatureManagementTranslationKeys.TOAST_DISABLED_ALL_ORGS, {
                featureName: getFeatureDisplayName(feature, t),
            }));
            setIsToastVisible(true);
        });
        setDisableModalState({ isOpen: false, feature: null });
    }, [disableModalState, onToggleFeature, onSaveFeatureOrgs, t]);
    const handleDisableDismiss = useCallback(() => {
        setDisableModalState({ isOpen: false, feature: null });
    }, []);
    const isFeatureEnabled = (feature) => feature.enabled !== undefined
        ? feature.enabled
        : !!(feature.orgs && feature.orgs.length > 0);
    const categoryGroups = useMemo(() => {
        const groupMap = new Map();
        const order = [];
        features.forEach(feature => {
            const cat = feature.category || 'Other';
            if (!groupMap.has(cat)) {
                groupMap.set(cat, []);
                order.push(cat);
            }
            const group = groupMap.get(cat);
            if (group)
                group.push(feature);
        });
        return order.map(name => ({
            name,
            features: groupMap.get(name) || [],
        }));
    }, [features]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ContentGrid, { as: "section", span: 10, grow: true },
            React.createElement(Horizontal, { vAlignContent: "center", spacing: "f", className: styles.toolbar },
                React.createElement("div", { className: styles.searchWrapper },
                    React.createElement(SearchBar, { searchValue: searchValue, searchPlaceholder: t(FeatureManagementTranslationKeys.SEARCH_PLACEHOLDER), onSearchChange: handleSearchChange, "data-testid": "feature-search" }))),
            React.createElement(LoadingIndicator.Global, { hideLoader: !loading, className: styles.loader },
                React.createElement("div", { className: styles.contentArea },
                    features.length === 0 && !loading && (React.createElement("div", { className: styles.featureMgmtEmptyState },
                        React.createElement(MutedAlert, { global: true, imageVisible: true, errorCode: ErrorCode.GLOBAL_SEARCH, title: searchValue
                                ? t(FeatureManagementTranslationKeys.NO_SEARCH_RESULTS_TITLE)
                                : t(FeatureManagementTranslationKeys.NO_FEATURES_TITLE), description: searchValue
                                ? t(FeatureManagementTranslationKeys.NO_SEARCH_RESULTS_DESCRIPTION)
                                : t(FeatureManagementTranslationKeys.NO_FEATURES_DESCRIPTION) }))),
                    features.length > 0 &&
                        selectedTab === FeatureAccessTab.EarlyAccess && (React.createElement(SectionContainer, { "data-testid": "early-access-section" }, features.map(feature => (React.createElement(ConfigCard, { key: feature.id, inputType: InputType.EditOrgsWithToggle, title: getFeatureDisplayName(feature, t), checked: isFeatureEnabled(feature), name: `toggle-${feature.id}`, selectedOrgIds: getOrgIds(feature), allOrgs: allOrgs, onSave: saveHandlers.get(feature.id) ??
                            makeSaveHandler(feature), onDisable: () => handleDisableTrigger(feature), learnMoreLink: feature.docsUrl, featureDescription: feature.description, "data-testid": `feature-card-${feature.id}` }))))),
                    features.length > 0 &&
                        selectedTab !== FeatureAccessTab.EarlyAccess &&
                        categoryGroups.map(group => (React.createElement(SectionContainer, { key: group.name, title: t(`${ConfigPrePostFixStrings.TSCLI_CONFIG}.${group.name}.${ConfigPrePostFixStrings.TITLE}`), isCollapsible: true, "data-testid": `category-${group.name}` }, group.features.map(feature => (React.createElement(ConfigCard, { key: feature.id, inputType: InputType.EditOrgs, title: getFeatureDisplayName(feature, t), description: feature.description, selectedOrgIds: getOrgIds(feature), allOrgs: allOrgs, onSave: saveHandlers.get(feature.id) ??
                                makeSaveHandler(feature), learnMoreLink: feature.docsUrl, "data-testid": `feature-card-${feature.id}` }))))))))),
        React.createElement(DisableFeatureModal, { isOpen: disableModalState.isOpen, featureName: disableModalState.feature
                ? getFeatureDisplayName(disableModalState.feature, t)
                : '', onDismiss: handleDisableDismiss, onConfirm: handleDisableConfirm }),
        React.createElement(GlobalAlert.Toast, { isVisible: isToastVisible, message: toastMessage, onClose: () => {
                setIsToastVisible(false);
                setToastMessage('');
            }, autoHide: true, showCheckmarkIcon: true })));
};
//# sourceMappingURL=feature-management.js.map